import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { Card, Col, Form, Row, Container, Spinner } from 'react-bootstrap';
import { cloneDeep } from 'lodash';
import toaster from 'src/utils/toaster';
import { useApiClient } from 'src/hooks/useApiClient';
import { useErrorToast } from 'src/hooks/useErrorToast';
import { isMarket } from 'src/utils/markets';
import { Markets } from 'src/types';
import { emptyStringsToNull } from '../common/utils';
import { GTMsendEvent } from 'src/utils/trackingGA4';
import Breadcrumb from 'src/components/Breadcrumb/Breadcrumb';
import Btn from 'src/components/Btn/Btn';
import { SegmentedControl } from 'src/components/SegmentedControl/SegmentedControl';
import MotornetVehicleSelect from '../common/MotornetVehicleSelect';
import MotornetVehicleSelector, {
	type MotornetSelectedVehicle,
} from '../common/MotornetVehicleSelector';
import { productionFetchParams } from '../common/selectorMeta';
import { MIN_MANUFACTURE_CODE_LENGTH } from '../common/useVehicleAutosuggest';
import { ApiError, OptionalDTO } from 'src/api';
import type {
	MotornetVehicleResponse,
	MotornetVehicleOption,
	ColorOption,
	OptionalCatalogItem,
	PlatformVehicleImportRequest,
	PlatformVehicleImportDetail,
	PlatformVehicleImportResponse,
} from '../types';
import { useMotornetVehicle } from '../common/useMotornetVehicle';
import MotornetOptionalsPicker from './MotornetOptionalsPicker';
import AutovistaOptionalsPicker from './AutovistaOptionalsPicker';
import {
	motornetToIdentity,
	paintOptions,
	interiorColorOptions,
	isInteriorColorRequired,
	optionalCatalog,
	toOptionalDTO,
} from '../common/motornetToForm';
import { colorFromOptionals } from '../common/colorOptionals';
import { useDealerOptions } from '../common/useDealerOptions';
import {
	emptyInstanceValues,
	validateInstance,
	mapLimitedVisibility,
	newOptional,
} from '../common/vehicleForm';
import OptionalsCard from 'src/components/OptionalsCard/OptionalsCard';
import MotornetVehicleFormFields from '../common/MotornetVehicleFormFields';
// @ts-ignore
import './MotornetVehiclePage.scss';

/**
 * Il picker e i select colore mostrano le stesse voci di listino (vernici e
 * interni a pagamento): dopo una spunta nel picker riallineiamo il select.
 */
const syncColorField = (
	optionals: OptionalCatalogItem[],
	pool: ColorOption[],
	currentCode: string,
	codeField: string,
	descriptionField: string,
	setFieldValue: (field: string, value: any) => void
) => {
	const next = colorFromOptionals(optionals, pool, currentCode);
	if (next === undefined) return;
	setFieldValue(codeField, next?.value ?? '');
	setFieldValue(descriptionField, next?.description ?? '');
};

const MotornetCreateVehiclePage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { apiClient } = useApiClient();
	const { showError } = useErrorToast();

	/**
	 * La ricerca per codice costruttore non e' disponibile sul mercato ES:
	 * resta la sola cascata marca / modello / allestimento.
	 */
	const canSearchByCode = !isMarket(Markets.es);

	/**
	 * Autovista non pubblica vincoli fra equipaggiamenti: su ES il picker e'
	 * quello senza validazione, per il resto il flusso e' lo stesso di IT.
	 */
	const isEsMarket = isMarket(Markets.es);

	const [selected, setSelected] = useState<MotornetVehicleOption | null>(null);
	const [pickMode, setPickMode] = useState<'search' | 'browse'>(
		canSearchByCode ? 'search' : 'browse'
	);
	const [picked, setPicked] = useState<MotornetSelectedVehicle>({
		brand: null,
		model: null,
		version: null,
	});
	const [externalErrors, setExternalErrors] = useState<any>(null);
	const [externalWarnings, setExternalWarnings] = useState<any>(null);
	const dealerOptions = useDealerOptions();

	const { data, isFetching, isError } = useMotornetVehicle(
		selected?.value ?? null,
		selected?.vehicleKind,
		selected?.year ?? null,
		selected?.month ?? null
	);
	const vehicle: MotornetVehicleResponse | null = data ?? null;

	const identity = useMemo(
		() => (vehicle ? motornetToIdentity(vehicle) : null),
		[vehicle]
	);
	const paints = useMemo<ColorOption[]>(
		() => (vehicle ? paintOptions(vehicle) : []),
		[vehicle]
	);
	const interiorColors = useMemo<ColorOption[]>(
		() => (vehicle ? interiorColorOptions(vehicle) : []),
		[vehicle]
	);
	const interiorColorRequired = isInteriorColorRequired(interiorColors);
	const optCatalog = useMemo(
		() => (vehicle ? optionalCatalog(vehicle, { includePackages: true }) : []),
		[vehicle]
	);
	/**
	 * Su ES l'equipaggiamento di serie e' selezionabile come il resto: entra sia
	 * nel picker sia nel catalogo che i select colore usano per riportare la
	 * vernice scelta fra gli optional.
	 */
	const esOptCatalog = useMemo(
		() =>
			vehicle
				? optionalCatalog(vehicle, {
					includePackages: true,
					includeStandard: true,
				})
				: [],
		[vehicle]
	);

	const initialValues = useMemo(() => {
		if (!identity) return null;
		return {
			...emptyInstanceValues(),
			codice_costruttore: identity.codice_costruttore,
			codice_motornet: identity.codice_motornet,
			codice_eurotax: identity.codice_eurotax,
			prezzo_listino: identity.prezzo_listino,
			...(isEsMarket && { custom_optionals: [{ ...newOptional }] }),
		};
	}, [identity, isEsMarket]);

	const buildBody = (values: any): PlatformVehicleImportRequest => {
		const v = cloneDeep(values);
		const seenCodici = new Set<string>();
		// Le voci di listino scelte nel picker e quelle digitate a mano (solo ES)
		// finiscono nella stessa lista: vince la prima, a parita' di codice.
		v.optionals = [
			...(v.optionals ?? []).map((o: OptionalCatalogItem) => toOptionalDTO(o)),
			...(v.custom_optionals ?? []),
		].filter((o: OptionalDTO) => {
			if (!o.codice_costruttore || seenCodici.has(o.codice_costruttore))
				return false;
			seenCodici.add(o.codice_costruttore);
			return true;
		});
		delete v.custom_optionals;
		v.limited_visibility = mapLimitedVisibility(v.limited_visibility);
		const nr_ordine_list: string[] = v.nr_ordine_list;
		const visibility_tag = v.visibility_tag;
		delete v.nr_ordine_list;

		const dettaglio_veicolo = emptyStringsToNull({
			...v,
			codice_motornet: v.codice_motornet,
			codice_eurotax: v.codice_eurotax,
		}) as PlatformVehicleImportDetail;

		return {
			dettaglio_veicolo,
			nr_ordine_list,
			is_checked: true,
			...(visibility_tag && { visibility_tag }),
		} as PlatformVehicleImportRequest;
	};

	const handleApiError = (err: ApiError) => {
		const errors = (err?.body as any)?.errors;
		const warnings = (err?.body as any)?.warnings;
		if (errors) setExternalErrors(errors);
		if (warnings) setExternalWarnings(warnings);
		if (!errors && !warnings) {
			showError(err, i18next.t('data_entry_error'));
			return;
		}
		if (errors) toaster.error(errors?.dup_key || i18next.t('data_entry_error'));
	};

	const onSubmitFn = (values: any, { setSubmitting }: any) => {
		setSubmitting(true);
		setExternalErrors(null);
		setExternalWarnings(null);
		const body = buildBody(values);
		apiClient.motornetControllerService
			.importVehicle(body)
			.then((res: PlatformVehicleImportResponse) => {
				res?.warnings?.forEach((w) => toaster.warning(w.message));
				toaster.success(i18next.t('created_vehicle_success'));
				GTMsendEvent({
					event: 'complete_add_vehicle',
					item_id: values.nr_ordine_list?.[0],
					dealer: values.dealer,
					item_category3: values.codice_costruttore,
				});
				navigate('/available-vehicles');
			})
			.catch((err: ApiError) => handleApiError(err))
			.finally(() => setSubmitting(false));
	};

	return (
		<Container fluid className="motornet-vehicle-page">
			<Breadcrumb
				item1={{
					name: i18next.t('vehicles_list'),
					link: '/available-vehicles',
				}}
				activeItem={{ name: i18next.t('add') }}
			/>
			<div className="display-6 my-4">{i18next.t('add_vehicle')}</div>

			<Card className="mt-2">
				<Card.Body>
					{/* Su ES resta la sola cascata, quindi niente selettore di modalita'. */}
					{canSearchByCode && (
						<div className="mb-3">
							<SegmentedControl
								fullWidth
								value={pickMode}
								onChange={(mode) => {
									setPickMode(mode as 'search' | 'browse');
									setPicked({ brand: null, model: null, version: null });
									setSelected(null);
									setExternalErrors(null);
									setExternalWarnings(null);
								}}
								data={[
									{
										value: 'search',
										label: t('motornet_pick_by_code', 'Cerca per codice costruttore'),
									},
									{
										value: 'browse',
										label: t(
											'motornet_pick_by_model',
											'Marca / Modello / Allestimento'
										),
									},
								]}
							/>
						</div>
					)}

					{pickMode === 'search' ? (
						<Row className="eurotax-select-row">
							<Col>
								<Form.Group className="mb-2">
									<Form.Label>
										{t(
											'motornet_search_label',
											'Cerca veicolo (codice costruttore)'
										)}
										<span className="red-ast">*</span>
									</Form.Label>
									<MotornetVehicleSelect
										value={selected}
										onSelect={(opt) => {
											setSelected(opt);
											setExternalErrors(null);
											setExternalWarnings(null);
										}}
									/>
									<Form.Text className="text-muted">
										{t('motornet_search_hint', {
											defaultValue:
												'Digita almeno {{count}} caratteri del codice costruttore',
											count: MIN_MANUFACTURE_CODE_LENGTH,
										})}
									</Form.Text>
								</Form.Group>
							</Col>
						</Row>
					) : (
						<Row className="g-3">
							<MotornetVehicleSelector
								showSelectLabels
								requiredVersion
								selectedVehicle={picked}
								onBrandChange={(brand) => {
									setPicked({ brand, model: null, version: null });
									setSelected(null);
								}}
								onModelChange={(model) => {
									setPicked((p) => ({ ...p, model, version: null }));
									setSelected(null);
								}}
								onVersionChange={(version) => {
									setPicked((p) => ({ ...p, version }));
									const eurotax = version?.data?.codiceEurotax;
									const vehicleKind =
										(picked.brand as any)?.data?.vehicleKind ?? 'auto';
									setSelected(
										eurotax
											? ({
												value: eurotax,
												label: version?.label ?? eurotax,
												vehicleKind,
												...productionFetchParams(version?.data, picked.model?.data),
											} as MotornetVehicleOption)
											: null
									);
									setExternalErrors(null);
									setExternalWarnings(null);
								}}
							/>
						</Row>
					)}
				</Card.Body>
			</Card>

			{isFetching && (
				<div className="my-4">
					<Spinner animation="border" size="sm" />{' '}
					{t('eurotax_vehicleSelect_loading', 'Ricerca…')}
				</div>
			)}

			{selected && isError && !isFetching && (
				<div className="my-4 text-danger">
					{t(
						'motornet_vehicle_resolve_error',
						'Impossibile caricare i dettagli del veicolo.'
					)}
				</div>
			)}

			{initialValues && (
				<Formik
					enableReinitialize
					initialValues={initialValues}
					validate={(values) =>
						validateInstance(values, { ordersMode: 'multi', interiorColorRequired })
					}
					onSubmit={onSubmitFn}
				>
					{({
						values,
						errors,
						touched,
						handleChange,
						handleBlur,
						handleSubmit,
						isSubmitting,
						setFieldValue,
						setFieldTouched,
					}) => (
						<Form>
							<MotornetVehicleFormFields
								values={values}
								errors={errors}
								touched={touched}
								handleChange={handleChange}
								handleBlur={handleBlur}
								setFieldValue={setFieldValue}
								setFieldTouched={setFieldTouched}
								dealerOptions={dealerOptions}
								paints={paints}
								interiorColors={interiorColors}
								interiorColorRequired={interiorColorRequired}
								ordersMode="multi"
								externalErrors={externalErrors}
								externalWarnings={externalWarnings}
								optionalsCatalog={isEsMarket ? esOptCatalog : optCatalog}
								optionalsSlot={
									isEsMarket ? (
										<>
											<AutovistaOptionalsPicker
												catalog={esOptCatalog}
												value={values.optionals as unknown as OptionalCatalogItem[]}
												onChange={(next) => setFieldValue('optionals', next)}
											/>
											<div className="display-7 my-4">
												{t('accessories_and_additional_optionals')}
											</div>
											<OptionalsCard
												name="custom_optionals"
												values={values}
												handleChange={handleChange}
												addOptionDisabled={false}
												onOptionAdded={() => {}}
											/>
										</>
									) : (
										<MotornetOptionalsPicker
											catalog={optCatalog}
											value={values.optionals as unknown as OptionalCatalogItem[]}
											onChange={(next) => {
												setFieldValue('optionals', next);
												// syncColorField(
												// 	next,
												// 	paints,
												// 	values.codice_colore_esterno,
												// 	'codice_colore_esterno',
												// 	'colore_esterno',
												// 	setFieldValue
												// );
												// syncColorField(
												// 	next,
												// 	interiorColors,
												// 	values.codice_colore_interno,
												// 	'codice_colore_interno',
												// 	'colore_interno',
												// 	setFieldValue
												// );
											}}
											accessories={vehicle?.accessories}
											eurotaxCode={identity?.codice_eurotax ?? ''}
											vehicleKind={selected?.vehicleKind ?? 'auto'}
											exteriorColor={
												paints.find(
													(p) => p.value === values.codice_colore_esterno
												) ?? null
											}
											interiorColor={
												interiorColors.find(
													(p) => p.value === values.codice_colore_interno
												) ?? null
											}
										/>
									)
								}
							/>

							<Row>
								<Col className="mt-4 text-end">
									<Btn
										icon="arrow-right"
										disabled={isSubmitting}
										onClick={() => {
											setExternalErrors(null);
											setExternalWarnings(null);
											handleSubmit();
											Object.keys(errors).length &&
												toaster.error(i18next.t('errors_in_submitted_fields'));
										}}
									>
										{t('save')}
									</Btn>
								</Col>
							</Row>
						</Form>
					)}
				</Formik>
			)}
		</Container>
	);
};

export default MotornetCreateVehiclePage;
